import { Alert, Card, createStyles, Group, Space, Text } from '@mantine/core';
import { AnchorButton } from '@root/Design/Primitives';
import { colorPalette } from '@root/Design/Themes';
import { useDi } from '@root/Services/DI';
import { ResourceService } from '@root/Services/Resources/ResourceService';
import { Fragment, useEffect, useState } from 'react';
import { ResourceTagService } from '../Services/ResourceTagService';
import { BulkTagProps } from './BulkTagProps';
import { TagKeyNames, TagEditor } from './TagEditor';
import { TagField } from './TagField';
import { CreateLabelKey, validateLabelKey, LABEL_PREFIX } from '@root/Components/Resources/Gcp/LabelUtils';

export const RenameLabel = (props: BulkTagProps) => {
    const [labelKeyList, setLabelKeyList] = useState<string[]>([]);
    const [allLabelKeyList, setAllLabelKeyList] = useState<string[]>([]);
    const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
    const { classes, theme } = useStyles();
    const resourceSvc = useDi(ResourceService);
    const [showOverwriteWarning, setShowOverwriteWarning] = useState<boolean>(false);
    const [selectedKeyError, setSelectedKeyError] = useState<string>('');
    const [renameKeys, setRenameKeys] = useState<TagKeyNames[]>([{ OldTagKey: '', NewTagKey: '' }]);
    const [canAdd, setCanAdd] = useState<boolean>(true);
    const [canRemove, setCanRemove] = useState<boolean>(false);
    const tagSvc = useDi(ResourceTagService);
    const [overwriteConflicts, setOverwriteConflicts] = useState<boolean>(true);
    const [deleteRenamedTags, setDeleteRenamedTags] = useState<boolean>(false);
    const [labelErrors, setLabelErrors] = useState<Map<number, string>>(new Map());

    useEffect(() => {
        (async () => {
            const keys = await tagSvc.getValidKeys(props.resourceIds);
            const labelKeys = keys.filter((key) => key.startsWith(LABEL_PREFIX)).map((key) => key.substring(6));
            setLabelKeyList(labelKeys);

            const allKeys = (await resourceSvc.getTags())
                .filter((key) => key.startsWith(LABEL_PREFIX))
                .map((key) => key.substring(6))
                .sort();
            setAllLabelKeyList(allKeys);
        })();
    }, [props.resourceIds.count()]);

    useEffect(() => {
        if (props.tagstoUpdate && props.updateToValue) {
            setRenameKeys(props.tagstoUpdate.map((t) => ({ OldTagKey: t, NewTagKey: props.updateToValue! })));
        }
    }, []);

    useEffect(() => {
        checkCanAdd();
    }, [labelKeyList]);

    useEffect(() => {
        const validForm = formIsValid();
        setDisableSubmit(!validForm);
        checkCanAdd();
        checkCanRemove();

        const newErrors = new Map<number, string>();
        renameKeys.forEach((rename, index) => {
            if (rename.NewTagKey) {
                const error = validateLabelKey(rename.NewTagKey);
                if (error) {
                    newErrors.set(index, error);
                }
            }
        });
        setLabelErrors(newErrors);
    }, [JSON.stringify(renameKeys), props.resourceIds.count()]);

    const checkCanAdd = () => {
        setCanAdd(renameKeys.length < labelKeyList.length);
    };

    const checkCanRemove = () => {
        setCanRemove(renameKeys.length > 1);
    };

    const formIsValid = () => {
        return (
            renameKeys.filter((k) => k.NewTagKey === '' || k.OldTagKey === '').length === 0 &&
            renameKeys.filter((k) => k.NewTagKey === k.OldTagKey || validateLabelKey(k.NewTagKey ?? '') !== null).length === 0
        );
    };

    const setOldLabelKey = (index: number, key: string) => {
        setRenameKeys((prevState) => {
            let newState = [...prevState];
            let oldKey = newState[index];
            oldKey.OldTagKey = key;
            newState.splice(index, 1, oldKey);
            return newState;
        });
    };

    const setNewLabelKey = (index: number, key: string) => {
        setRenameKeys((prevState) => {
            let newState = [...prevState];
            let newKey = newState[index];
            newKey.NewTagKey = key;
            newState.splice(index, 1, newKey);
            return newState;
        });
    };

    const addRowHandler = () => {
        setRenameKeys((prevState) => {
            let newState = [...prevState];
            newState.push({ OldTagKey: '', NewTagKey: '' });
            return newState;
        });
    };

    const removeRowHandler = (index: number) => {
        setRenameKeys((prevState) => {
            let newState = [...prevState];
            newState.splice(index, 1);
            return newState;
        });
    };

    const getError = (index: number = 0) => {
        if (labelErrors.has(index) && labelErrors.get(index)) {
            return labelErrors.get(index);
        }
        return undefined;
    };

    const renamesWithPrefix = renameKeys.map((k) => ({
        OldTagKey: CreateLabelKey(k.OldTagKey),
        NewTagKey: CreateLabelKey(k.NewTagKey),
    }));

    const tagJob = tagSvc.createTagJob(props.resourceIds, {
        Renames: renamesWithPrefix,
        OverwriteConflicts: overwriteConflicts,
        DeleteRenamedTags: deleteRenamedTags,
    });

    return (
        <TagEditor
            onButtonClick={props.onButtonClick}
            companyId={props.companyId}
            resourceIds={props.resourceIds}
            pageTitle={props.pageTitle}
            instructions={props.instructions}
            canSubmit={!disableSubmit}
            setDisableSubmit={setDisableSubmit}
            overwriteConflicts={!overwriteConflicts}
            setOverwriteConflicts={setOverwriteConflicts}
            operationType="renameKey"
            tagJob={tagJob}
            onTagJobStarting={props.onTagJobStarting}
            onTagJobStarted={props.onTagJobStarted}
            setDeleteRenamedTags={setDeleteRenamedTags}
        >
            <>
                {renameKeys.map((label, index) => (
                    <Fragment key={`keyFragment${index}`}>
                        <Card withBorder radius="md" p="sm">
                            <Group noWrap grow spacing={0}>
                                <TagField
                                    atid={'LabelNewKeyInput:' + index}
                                    key={`newKey${index}`}
                                    children={
                                        <TagField
                                            atid={'LabelKeyInput:' + index}
                                            key={`oldKey${index}`}
                                            fieldValue={renameKeys[index].OldTagKey}
                                            valuePickList={labelKeyList.filter((k) => !renameKeys.map((m) => m.OldTagKey).includes(k))}
                                            label="Select Label"
                                            setSelectedValue={setOldLabelKey}
                                            canAdd={false}
                                            canRemove={false}
                                            index={index}
                                            count={renameKeys.length}
                                            receiveInitalFocus={true}
                                            type="key"
                                        />
                                    }
                                    fieldValue={renameKeys[index].NewTagKey}
                                    valuePickList={allLabelKeyList}
                                    label={deleteRenamedTags ? 'Rename To' : 'Copy To'}
                                    setSelectedValue={setNewLabelKey}
                                    canAdd={false}
                                    canRemove={canRemove}
                                    index={index}
                                    count={renameKeys.length}
                                    handleAddRow={addRowHandler}
                                    handleRemoveRow={removeRowHandler}
                                    type="key"
                                    error={getError(index)}
                                />
                            </Group>
                        </Card>
                        <Space h="sm" />
                    </Fragment>
                ))}
                <Card withBorder radius="md" p="sm">
                    <AnchorButton text="Add" onClick={addRowHandler} icon={<i className="ti ti-circle-plus"></i>} atid={'RenameLabelKeysAddAnchor'} />
                </Card>
                {showOverwriteWarning && !disableSubmit ? (
                    <Alert
                        icon={<i className="ti ti-alert-triangle" color="warning" />}
                        sx={{ marginTop: theme.spacing.lg, borderRadius: theme.radius.sm }}
                        title="Are you sure?"
                        color="warning"
                    >
                        One or more of your selected resources already has the target label. Renaming will over-write these label values.
                    </Alert>
                ) : null}
                <Text color="error" hidden={selectedKeyError == undefined || selectedKeyError.length === 0}>
                    *{selectedKeyError}
                </Text>
            </>
        </TagEditor>
    );
};

const useStyles = createStyles((theme) => ({
    textColor: {
        color: colorPalette.subHeaderTextColor,
        alignContent: 'center',
        i: {
            alignItems: 'center',
        },
    },
    footer: {
        position: 'absolute',
        bottom: 0,
    },

    keyValues: {
        marginTop: `${theme.spacing.xs}px`,
    },
}));
